import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import { useThemeMode } from '../../../contexts/ThemeModeContext';
import { dataGridHeaderSx } from '../../../theme/themes';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../../contexts/AuthContext';

type DeviceLog = {
  id: string;
  deviceId: string;
  logLevel: string;
  logType: string;
  message: string;
  metadata: string | null;
  logDate: string;
};

interface DeviceLogsTabProps {
  deviceId: string;
}

const DeviceLogsTab: React.FC<DeviceLogsTabProps> = ({ deviceId }) => {
  const auth = useAuth();
  const { mode } = useThemeMode();
  const accessToken = auth.tokens?.accessToken;
  const [logs, setLogs] = useState<DeviceLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'logDate', sort: 'desc' },
  ]);

  // Filters
  const [logType, setLogType] = useState('');
  const [logLevel, setLogLevel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const skip = paginationModel.page * paginationModel.pageSize;
      const take = paginationModel.pageSize;
      const sortBy = sortModel[0]?.field || 'logDate';
      const sortOrder = sortModel[0]?.sort || 'desc';

      const params = new URLSearchParams({
        skip: skip.toString(),
        take: take.toString(),
        sortBy,
        sortOrder,
      });

      if (logType) params.append('logType', logType);
      if (logLevel) params.append('logLevel', logLevel);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await fetch(
        `/api/devices/${deviceId}/logs?${params}`,
        { headers: authHeader }
      );
      if (!res.ok) throw new Error('Failed to fetch logs');

      const responseData = await res.json();
      const data: DeviceLog[] = responseData.data || [];
      const total = responseData.total || 0;

      setLogs(data);
      setTotalCount(total);
    } catch (e: any) {
      setError(e.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, paginationModel, sortModel, logType, logLevel, startDate, endDate]);

  const handleDelete = async (id: string) => {
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/devices/${deviceId}/logs/${id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!res.ok) throw new Error('Failed to delete log');
      setSuccess('Log deleted successfully');
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to delete log');
    }
  };

  const handleDeleteAll = async () => {
    try {
      const res = await fetch(
        `/api/devices/${deviceId}/logs/all`,
        { method: 'DELETE', headers: authHeader }
      );

      if (!res.ok) {
        if (res.status === 403) {
          setError('Only administrators can delete all logs');
        } else {
          throw new Error('Delete all failed');
        }
        setDeleteAllConfirmOpen(false);
        return;
      }

      setSuccess('All logs deleted successfully');
      await load();
      setDeleteAllConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting all logs:', err);
      setError('Failed to delete all logs');
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return 'error';
      case 'WARNING':
        return 'warning';
      case 'INFO':
        return 'info';
      case 'DEBUG':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'logDate',
      headerName: 'Date/Time',
      width: 180,
      renderCell: (params) => {
        const raw = params.row.logDate;
        if (!raw) return <span>—</span>;
        // Trim fractional seconds to 3 digits so JS Date.parse handles it
        const normalised = String(raw).replace(/(\.\d{3})\d+/, '$1');
        const date = new Date(normalised);
        if (isNaN(date.getTime())) return <span>—</span>;
        return (
          <span>
            {date.toLocaleString('en-AU', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit', second: '2-digit',
              hour12: false,
            })}
          </span>
        );
      },
    },
    {
      field: 'logLevel',
      headerName: 'Level',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getLogLevelColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'logType',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => (
        <Chip label={params.value} variant="outlined" size="small" />
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 1,
      minWidth: 300,
    },
    {
      field: 'metadata',
      headerName: 'Details',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        if (!params.value) return null;
        try {
          const metadata = JSON.parse(params.value);
          const entries = Object.entries(metadata).filter(
            ([, v]) => v !== null && v !== undefined && !Array.isArray(v) && typeof v !== 'object'
          );
          const summary = entries
            .map(([k, v]) => `${k}: ${v}`)
            .join(' | ');
          const fullText = Object.entries(metadata)
            .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
            .join('\n');
          return (
            <Tooltip title={<pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>{fullText}</pre>} arrow>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', cursor: 'default' }}>
                {summary || `${Object.keys(metadata).length} fields`}
              </Typography>
            </Tooltip>
          );
        } catch {
          return (
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{params.value}</Typography>
          );
        }
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 80,
      getActions: (params: any) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  const isAdmin = auth.hasRole('DMO_ADMIN');

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Device Logs</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            size="small"
          >
            Refresh
          </Button>
          {isAdmin && totalCount > 0 && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteAllConfirmOpen(true)}
              size="small"
            >
              Delete All
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Log Type"
          value={logType}
          onChange={(e) => {
            setLogType(e.target.value);
            setPaginationModel({ ...paginationModel, page: 0 });
          }}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="FTP_UPLOAD">FTP Upload</MenuItem>
          <MenuItem value="INFERENCE">Inference</MenuItem>
          <MenuItem value="RELAY_TRIGGER">Relay Trigger</MenuItem>
          <MenuItem value="DETECTION">Detection</MenuItem>
          <MenuItem value="TEMPERATURE">Temperature</MenuItem>
          <MenuItem value="ERROR">Error</MenuItem>
          <MenuItem value="SYSTEM">System</MenuItem>
        </TextField>
        
        <TextField
          select
          label="Log Level"
          value={logLevel}
          onChange={(e) => {
            setLogLevel(e.target.value);
            setPaginationModel({ ...paginationModel, page: 0 });
          }}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Levels</MenuItem>
          <MenuItem value="INFO">Info</MenuItem>
          <MenuItem value="WARNING">Warning</MenuItem>
          <MenuItem value="ERROR">Error</MenuItem>
          <MenuItem value="DEBUG">Debug</MenuItem>
        </TextField>

        <TextField
          type="date"
          label="Start Date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPaginationModel({ ...paginationModel, page: 0 });
          }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        
        <TextField
          type="date"
          label="End Date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPaginationModel({ ...paginationModel, page: 0 });
          }}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        
        {(logType || logLevel || startDate || endDate) && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setLogType('');
              setLogLevel('');
              setStartDate('');
              setEndDate('');
              setPaginationModel({ ...paginationModel, page: 0 });
            }}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={logs}
          columns={columns}
          loading={loading}
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[25, 50, 100]}
          sx={dataGridHeaderSx(mode)}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={deleteAllConfirmOpen} onClose={() => setDeleteAllConfirmOpen(false)}>
        <DialogTitle>Delete All Logs</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>all {totalCount} logs</strong> for this device?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAll} color="error" variant="contained">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceLogsTab;
